import { SelectOutlined, TextInput } from '@amelio/ui-library';
import { Controller, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { KEY_RESULT_UNIT_LABEL_KEYS } from '../objectiveLabels';
import { CURRENCY_SYMBOL_MAX_LENGTH } from '../validation';

import type { ObjectiveFormData } from '../validation';
import type { ReactNode } from 'react';
import type { Control } from 'react-hook-form';

import { NumberInput } from '@/components/NumberInput/NumberInput';
import { KeyResultUnit } from '@/constants/KeyResultUnit';
import { DEFAULT_CURRENCY_SYMBOL } from '@/utils/objectivesFormat';
import { validationMessageKey } from '@/utils/validationMessage';

/** `TextInput` puts its classes on the field's frame, not on the `input`, which does not inherit
 * `text-align`: the centring has to reach the input itself. */
const CENTERED_VALUE = '[&_input]:text-center';

const SMALL_VALUE_FIELD_CLASS = `border-obj-line bg-surface-field min-w-[61px] rounded-[8px] ${CENTERED_VALUE}`;
const SELECT_FIELD_WIDTH = 'min-w-[180px]';

/** One height for the whole row: the library defaults differ between `TextInput` (48px) and
 * `SelectOutlined` (40px), which left the captioned values sitting at two heights side by side. */
const FIELD_SIZE = 'md';

/** One text size for the whole row, next to its 12px captions: the library's `md` scale renders
 * `text-md`, two pixels above the size the reference measures inside these fields. */
const FIELD_TEXT_SIZE = 'sm';

interface FieldLabelProps {
    label: string;
    /** Left out for the unit select: `SelectOutlined` ignores `id`, so it carries an `ariaLabel`
     * rather than an association the DOM could not honour. */
    htmlFor?: string;
    children: ReactNode;
}

/** Every small field is captioned by a 12px grey label sitting directly above its control, so a
 * row of them reads as one wrapping line of captioned values. */
const FieldLabel = ({ label, htmlFor, children }: FieldLabelProps) => (
    <div className={'flex flex-col gap-1.5'}>
        <label
            htmlFor={htmlFor}
            className={'text-obj-gray text-[12px] font-normal tracking-[0.11px]'}
        >
            {label}
        </label>
        {children}
    </div>
);

interface KeyResultMeasurementFieldsProps {
    control: Control<ObjectiveFormData>;
    index: number;
    isYesNo: boolean;
    isMonetary: boolean;
    onUnitChange: (nextUnit: string, onChange: (value: string) => void) => void;
    onStartChange: (nextStart: number, onChange: (value: number) => void) => void;
}

/** What the measurement measures and between which bounds: its unit, its currency symbol, where
 * it starts and where it is aiming. */
export default function KeyResultMeasurementFields({
    control,
    index,
    isYesNo,
    isMonetary,
    onUnitChange,
    onStartChange,
}: KeyResultMeasurementFieldsProps) {
    const { t } = useTranslation();

    const unitOptions = Object.values(KeyResultUnit).map((value) => ({
        id: value,
        value,
        label: t(KEY_RESULT_UNIT_LABEL_KEYS[value]),
    }));

    const startName = `keyResults.${index}.startValue` as const;
    const targetName = `keyResults.${index}.targetValue` as const;
    const { errors } = useFormState({ control, name: [startName, targetName] });
    const rangeErrors = errors.keyResults?.[index];
    // One message for the pair, under the row, so the fields stay in line.
    const rangeMessages = [
        ...new Set([rangeErrors?.startValue?.message, rangeErrors?.targetValue?.message]),
    ].filter((message) => message !== undefined);
    const rangeErrorId = `key-result-${index}-range-error`;

    return (
        <div className={'mb-6 flex flex-col gap-2'}>
            {/* Wraps like the detail page's row: five fields on one line overflow the card below ~900px. */}
            <div className={'flex flex-wrap items-start gap-x-[48px] gap-y-4'}>
                <FieldLabel label={t('objectives.form.unitLabel')}>
                    <Controller
                        name={`keyResults.${index}.unit`}
                        control={control}
                        render={({ field }) => (
                            <div className={SELECT_FIELD_WIDTH}>
                                <SelectOutlined
                                    size={FIELD_SIZE}
                                    textSize={FIELD_TEXT_SIZE}
                                    options={unitOptions}
                                    value={field.value}
                                    onChange={(value) => onUnitChange(value, field.onChange)}
                                    placeholder={t('objectives.form.selectUnit')}
                                    ariaLabel={t('objectives.form.unitLabel')}
                                    dataTestId={`key-result-${index}-unit`}
                                />
                            </div>
                        )}
                    />
                </FieldLabel>

                {isMonetary && (
                    <FieldLabel
                        label={t('objectives.form.currencySymbol')}
                        htmlFor={`key-result-${index}-currency`}
                    >
                        <Controller
                            name={`keyResults.${index}.currencySymbol`}
                            control={control}
                            render={({ field }) => (
                                <TextInput
                                    id={`key-result-${index}-currency`}
                                    size={FIELD_SIZE}
                                    textSize={FIELD_TEXT_SIZE}
                                    value={field.value ?? DEFAULT_CURRENCY_SYMBOL}
                                    onChange={field.onChange}
                                    maxLength={CURRENCY_SYMBOL_MAX_LENGTH}
                                    placeholder={t('objectives.form.currencySymbol')}
                                    ariaLabel={t('objectives.form.currencySymbol')}
                                    className={SMALL_VALUE_FIELD_CLASS}
                                    dataTestId={`key-result-${index}-currency`}
                                />
                            )}
                        />
                    </FieldLabel>
                )}

                {!isYesNo && (
                    <>
                        <FieldLabel
                            label={t('objectives.form.startValue')}
                            htmlFor={`key-result-${index}-start`}
                        >
                            <Controller
                                name={startName}
                                control={control}
                                rules={{ deps: [targetName] }}
                                render={({ field, fieldState }) => (
                                    <NumberInput
                                        id={`key-result-${index}-start`}
                                        size={FIELD_SIZE}
                                        textSize={FIELD_TEXT_SIZE}
                                        value={field.value}
                                        onChange={(value) => onStartChange(value, field.onChange)}
                                        placeholder={t('objectives.form.startValue')}
                                        ariaLabel={t('objectives.form.startValue')}
                                        error={!!fieldState.error}
                                        ariaDescribedBy={
                                            fieldState.error ? rangeErrorId : undefined
                                        }
                                        className={SMALL_VALUE_FIELD_CLASS}
                                        dataTestId={`key-result-${index}-start`}
                                    />
                                )}
                            />
                        </FieldLabel>

                        <FieldLabel
                            label={t('objectives.form.targetValue')}
                            htmlFor={`key-result-${index}-target`}
                        >
                            <Controller
                                name={targetName}
                                control={control}
                                rules={{ deps: [startName] }}
                                render={({ field, fieldState }) => (
                                    <NumberInput
                                        id={`key-result-${index}-target`}
                                        size={FIELD_SIZE}
                                        textSize={FIELD_TEXT_SIZE}
                                        value={field.value}
                                        onChange={field.onChange}
                                        placeholder={t('objectives.form.targetValue')}
                                        ariaLabel={t('objectives.form.targetValue')}
                                        error={!!fieldState.error}
                                        ariaDescribedBy={
                                            fieldState.error ? rangeErrorId : undefined
                                        }
                                        className={SMALL_VALUE_FIELD_CLASS}
                                        dataTestId={`key-result-${index}-target`}
                                    />
                                )}
                            />
                        </FieldLabel>
                    </>
                )}
            </div>

            {rangeMessages.length > 0 && (
                <div id={rangeErrorId}>
                    {rangeMessages.map((message) => (
                        <p
                            key={message}
                            role={'alert'}
                            className={'text-error text-sm'}
                        >
                            {t(validationMessageKey(message))}
                        </p>
                    ))}
                </div>
            )}
        </div>
    );
}
